import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Touch as ReactTouch,
  type TouchEvent as ReactTouchEvent,
  type TouchList as ReactTouchList,
} from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { GenerationResult } from '@gilding/shared';
import { STYLE_PROFILES } from '@gilding/shared';
import { api } from '../api/client.js';
import { cn } from '../lib/classnames.js';
import { Reveal } from '../components/Reveal.js';
import { GildButton } from '../components/GildButton.js';
import { GalleryLightbox } from '../components/GalleryLightbox.js';
import { ShareButton } from '../components/ShareButton.js';

type GenWithCounts = GenerationResult & { upvotes: number; remixes: number; onFeed?: boolean };

// ----------------------------------------------------------------------------
// Inline SVG icons. Bespoke; no lucide import tell.
// ----------------------------------------------------------------------------

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      className={cn('inline-block h-3.5 w-3.5', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1.5 8s2.4-4.5 6.5-4.5S14.5 8 14.5 8s-2.4 4.5-6.5 4.5S1.5 8 1.5 8z" />
      <circle cx="8" cy="8" r="1.8" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      className={cn('inline-block h-3.5 w-3.5', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3.5" y="7" width="9" height="6.5" rx="1" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </svg>
  );
}

/** Two-finger pinch glyph — an arrow drawing inward toward a centre point, the
 *  universal "pinch" cue. Hints the gallery's pinch-to-resize gesture. */
function PinchIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      className={cn('inline-block h-4 w-4', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="1.1" fill="currentColor" stroke="none" />
      <path d="M8 5.4 V2.2 M6.7 3.5 L8 2.2 L9.3 3.5" />
      <path d="M8 10.6 V13.8 M6.7 12.5 L8 13.8 L9.3 12.5" />
      <path d="M5.4 8 H2.2 M3.5 6.7 L2.2 8 L3.5 9.3" />
      <path d="M10.6 8 H13.8 M12.5 6.7 L13.8 8 L12.5 9.3" />
    </svg>
  );
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      className={cn('h-3.5 w-3.5', className)}
      fill="currentColor"
    >
      <circle cx="3.6" cy="8" r="1.05" />
      <circle cx="8" cy="8" r="1.05" />
      <circle cx="12.4" cy="8" r="1.05" />
    </svg>
  );
}

/**
 * Empty-state illustration: "unfinished-page" — a faded parchment rectangle
 * with a single ink stroke trailing off. Hand-drawn feel, no asset deps.
 */
function UnfinishedPageIllustration({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 160 160"
      aria-hidden="true"
      className={cn('h-32 w-32', className)}
      fill="none"
    >
      {/* The folded page */}
      <path
        d="M 38 22 L 112 22 L 122 32 L 122 138 L 38 138 Z"
        fill="rgb(var(--color-surface-elevated))"
        stroke="rgb(var(--color-hairline-strong))"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
      {/* Dog-eared corner fold */}
      <path
        d="M 112 22 L 112 32 L 122 32 Z"
        fill="rgb(var(--color-surface-sunken))"
        stroke="rgb(var(--color-hairline-strong))"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
      {/* Hairline rules — empty manuscript lines */}
      <line x1="50" y1="56" x2="100" y2="56" stroke="rgb(var(--color-hairline))" strokeWidth="0.6" />
      <line x1="50" y1="68" x2="108" y2="68" stroke="rgb(var(--color-hairline))" strokeWidth="0.6" />
      <line x1="50" y1="80" x2="92" y2="80" stroke="rgb(var(--color-hairline))" strokeWidth="0.6" />
      {/* The unfinished ink stroke — trails off mid-thought */}
      <path
        d="M 52 100 C 60 96, 72 104, 80 100 C 86 97, 92 102, 96 104"
        fill="none"
        stroke="rgb(var(--color-accent-deep))"
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity="0.78"
      />
      {/* The last drop of ink */}
      <circle cx="100" cy="105" r="1.2" fill="rgb(var(--color-accent-deep))" opacity="0.5" />
    </svg>
  );
}

// ----------------------------------------------------------------------------
// GalleryHeader — single source of truth for the gallery page identity. Used
// in the loading/empty branches so the same eyebrow + h1 don't appear in
// three places. The main rendered state inlines the h1 alongside the
// "Gild another" CTA so the action button can sit on the right edge.
// ----------------------------------------------------------------------------

function GalleryHeader() {
  return (
    <header>
      <h1 className="heading-display">Your gallery</h1>
    </header>
  );
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------

const GALLERY_PAGE = 50;

export function Gallery() {
  const queryClient = useQueryClient();
  // The piece open in the lightbox (null = closed).
  const [selected, setSelected] = useState<GenWithCounts | null>(null);
  // Paginated: the wallet can hold up to 500 gildings, so the gallery loads
  // them a page at a time rather than capping at the first 50.
  const galleryQuery = useInfiniteQuery({
    queryKey: ['mine'],
    queryFn: ({ pageParam }) => api.myGenerations(GALLERY_PAGE, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const publishMut = useMutation({
    mutationFn: (id: string) => api.publish(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mine'] }),
  });
  const unpublishMut = useMutation({
    mutationFn: (id: string) => api.unpublish(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mine'] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteGeneration(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mine'] });
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  if (galleryQuery.isLoading) {
    return (
      <div className="flex flex-col gap-10">
        <GalleryHeader />
        <p className="font-display italic text-ink-subtle">Drawing the leaves…</p>
      </div>
    );
  }

  // De-dupe by id: if a new piece shifts pagination offsets between page loads,
  // the same gilding can arrive twice — which would otherwise double-count the
  // "on the feed" tally and repeat a tile.
  const seenGen = new Set<string>();
  const items: GenWithCounts[] = (galleryQuery.data?.pages ?? [])
    .flatMap((p) => p.items)
    .filter((g) => (seenGen.has(g.id) ? false : (seenGen.add(g.id), true)));
  // The true count of everything kept (not just what's loaded).
  const total = galleryQuery.data?.pages[0]?.total ?? items.length;

  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-10">
        <GalleryHeader />
        <section className="flex flex-col items-center gap-5 py-10 text-center">
          <UnfinishedPageIllustration />
          <div className="flex max-w-md flex-col gap-2">
            <h2 className="heading-section">An unfinished page</h2>
            <p className="text-body text-ink-soft">
              Nothing gilded yet. Start with a sketch, even one line, and let it be amplified.
            </p>
          </div>
          <Link to="/new" className="btn-primary mt-2">
            Gild your first
          </Link>
        </section>
      </div>
    );
  }

  // Authoritative count of pieces actually ON the feed (from the server), so it
  // never over-counts a piece whose visibility flag drifted from feed reality.
  const onFeedCount =
    galleryQuery.data?.pages[0]?.onFeed ?? items.filter((g) => g.onFeed ?? g.visibility === 'public').length;

  return (
    <div className="flex flex-col gap-12">
      {/* ---- Page signature: editorial header ---- */}
      <header className="flex flex-col gap-6">
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="heading-display">Your gallery</h1>
          <GildButton to="/new">Gild another</GildButton>
        </Reveal>

        {/* Tally line — typography-led, not a row of stat cards */}
        <Reveal as="p" delay={180} className="font-display italic text-caption text-ink-subtle">
          <span className="text-ink-soft">{total}</span>{' '}
          {total === 1 ? 'gilding' : 'gildings'} so far
          {onFeedCount > 0 ? (
            <>
              {' '}· <span className="text-ink-soft">{onFeedCount}</span> on the feed
            </>
          ) : null}
        </Reveal>
      </header>

      {/* ---- The wall: a generous, gold-framed grid. Each piece keeps its own
              native aspect; tiles lift and their gilt edge warms on hover. ---- */}
      <section>
        {/* Mobile-only discoverability hint for pinch-to-resize the wall. */}
        <p className="mb-3 flex items-center justify-center gap-1.5 font-display text-caption italic text-ink-subtle sm:hidden">
          <PinchIcon className="h-4 w-4 text-accent-deep" />
          Pinch to resize the wall
        </p>
        <PremiumGrid
          items={items}
          onOpen={setSelected}
          onPublish={(id) => publishMut.mutate(id)}
          onUnpublish={(id) => unpublishMut.mutate(id)}
          onDelete={(id) => deleteMut.mutate(id)}
          isMutating={(id) =>
            (publishMut.isPending && publishMut.variables === id) ||
            (unpublishMut.isPending && unpublishMut.variables === id) ||
            (deleteMut.isPending && deleteMut.variables === id)
          }
        />

        <InfiniteFoot
          hasNext={galleryQuery.hasNextPage}
          fetching={galleryQuery.isFetchingNextPage}
          loaded={items.length}
          total={total}
          onReach={() => void galleryQuery.fetchNextPage()}
        />
      </section>

      {selected && (
        <GalleryLightbox
          gen={selected}
          onClose={() => setSelected(null)}
          onPublish={() => publishMut.mutate(selected.id)}
          onUnpublish={() => unpublishMut.mutate(selected.id)}
          onDelete={() => deleteMut.mutate(selected.id)}
          onChargeHdClip={async () => {
            try {
              await api.motionClip(selected.id, selected.coverIndex ?? 0);
              void queryClient.invalidateQueries({ queryKey: ['me'] });
              return { ok: true };
            } catch (e) {
              const code = e && typeof e === 'object' && 'code' in e ? String(e.code) : '';
              const msg = e && typeof e === 'object' && 'message' in e ? String(e.message) : '';
              const insufficient = code === 'insufficient_credits' || msg.includes('insufficient');
              return { ok: false, error: insufficient ? 'insufficient_credits' : code || 'charge_failed' };
            }
          }}
          mutating={
            (publishMut.isPending && publishMut.variables === selected.id) ||
            (unpublishMut.isPending && unpublishMut.variables === selected.id) ||
            (deleteMut.isPending && deleteMut.variables === selected.id)
          }
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// InfiniteFoot — seamless paging. A sentinel a screen below the last row
// prefetches the next page before the reader reaches the end, so the wall just
// keeps going. No load-bearing button, no jump — a quiet gilded tick while it
// fetches, then a calm "n of total".
// ----------------------------------------------------------------------------

function InfiniteFoot({
  hasNext,
  fetching,
  loaded,
  total,
  onReach,
}: {
  hasNext: boolean;
  fetching: boolean;
  loaded: number;
  total: number;
  onReach: () => void;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onReachRef = useRef(onReach);
  onReachRef.current = onReach;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNext || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onReachRef.current();
      },
      { rootMargin: '700px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
    // Re-arm after each fetch settles so the next page can prefetch in turn.
  }, [hasNext, fetching]);

  return (
    <div className="relative mt-10 flex min-h-6 flex-col items-center justify-center">
      {hasNext && (
        <div
          ref={sentinelRef}
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 h-px w-px"
        />
      )}
      {fetching ? (
        <span className="inline-flex items-center gap-2 font-display text-caption italic text-ink-subtle">
          <span className="h-1.5 w-1.5 animate-leaf-pulse rounded-full bg-accent" />
          Gilding more…
        </span>
      ) : total > 0 ? (
        <span className="font-display text-caption italic text-ink-subtle">
          <span className="text-ink-soft">{loaded}</span> of {total}
        </span>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------------------
// PremiumGrid — a clean, generous masonry of gold-framed tiles. Each piece keeps
// its native aspect; the tile lifts and its gilt hairline warms on hover, and
// the bitmap eases in as it decodes. Round-robin into independent flex columns
// so every column top-aligns and the wall never reflows as images stream in.
// ----------------------------------------------------------------------------

function PremiumGrid({
  items,
  onOpen,
  onPublish,
  onUnpublish,
  onDelete,
  isMutating,
}: {
  items: GenWithCounts[];
  onOpen: (gen: GenWithCounts) => void;
  onPublish: (id: string) => void;
  onUnpublish: (id: string) => void;
  onDelete: (id: string) => void;
  isMutating: (id: string) => boolean;
}) {
  // Auto column count from viewport width — the baseline until the viewer pinches.
  const [autoCols, setAutoCols] = useState(3);
  // Pinch override: once you pinch the wall, your chosen density sticks (null =
  // follow the responsive default). Pinch OUT → fewer, larger tiles; IN → more.
  const [userCols, setUserCols] = useState<number | null>(null);
  const cols = userCols ?? autoCols;
  const MIN_COLS = 1;
  const MAX_COLS = 6;

  useEffect(() => {
    const compute = (): void => {
      const w = window.innerWidth;
      setAutoCols(w < 540 ? 2 : w < 960 ? 3 : 4);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // ── Pinch-to-zoom the wall (touch) ──────────────────────────────────────
  // Read the browser's AUTHORITATIVE live touch list each event (e.touches)
  // rather than hand-tracking pointers in a Map — a Map desyncs whenever a
  // pointerup/cancel is missed (which `touch-action: pan-y` can cause), which
  // froze the gesture. With e.touches there's no bookkeeping to get stuck.
  const pinchBase = useRef<{ dist: number; cols: number } | null>(null);
  const colsRef = useRef(cols);
  colsRef.current = cols;
  const touchDist = (t: ReactTouchList): number => {
    if (t.length < 2) return 0;
    const a = t[0] as ReactTouch;
    const b = t[1] as ReactTouch;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };
  const onTouchStart = (e: ReactTouchEvent): void => {
    if (e.touches.length === 2) pinchBase.current = { dist: touchDist(e.touches), cols: colsRef.current };
  };
  const onTouchMove = (e: ReactTouchEvent): void => {
    if (e.touches.length !== 2) {
      pinchBase.current = null;
      return;
    }
    const s = touchDist(e.touches);
    // (Re)base if we don't have a baseline yet (e.g. a second finger just landed).
    if (!pinchBase.current || pinchBase.current.dist <= 0) {
      pinchBase.current = { dist: s, cols: colsRef.current };
      return;
    }
    // Step one column per ~20% change in finger spread, then RE-BASE to the new
    // spread + count. Symmetric (spread to zoom in ⇄ squeeze to zoom out) and
    // continuous, so you can keep pinching to step further — and zooming back out
    // from the max-zoom (single column) only needs a small squeeze, not a huge one.
    const base = pinchBase.current;
    const ratio = s / base.dist;
    if (ratio >= 1.2 && base.cols > MIN_COLS) {
      const next = base.cols - 1; // fingers apart → zoom in → fewer, larger tiles
      setUserCols(next);
      pinchBase.current = { dist: s, cols: next };
    } else if (ratio <= 0.83 && base.cols < MAX_COLS) {
      const next = base.cols + 1; // fingers together → zoom out → more, smaller tiles
      setUserCols(next);
      pinchBase.current = { dist: s, cols: next };
    }
  };
  const onTouchEnd = (e: ReactTouchEvent): void => {
    if (e.touches.length < 2) pinchBase.current = null;
  };

  return (
    <div
      className="flex gap-3.5 sm:gap-4"
      style={{ touchAction: 'pan-y' }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      {buildColumns(items, cols).map((col, ci) => (
        <div key={ci} className="flex min-w-0 flex-1 flex-col gap-3.5 sm:gap-4">
          {col.map((g) => (
            <GridCard
              key={g.id}
              gen={g}
              onOpen={() => onOpen(g)}
              onPublish={() => onPublish(g.id)}
              onUnpublish={() => onUnpublish(g.id)}
              onDelete={() => onDelete(g.id)}
              mutating={isMutating(g.id)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function GridCard({
  gen,
  onOpen,
  onPublish,
  onUnpublish,
  onDelete,
  mutating,
}: {
  gen: GenWithCounts;
  onOpen: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  mutating: boolean;
}) {
  const firstImage = gen.images[gen.coverIndex ?? 0]?.url ?? gen.images[0]?.url;
  const isPublic = gen.visibility === 'public';
  const onFeed = gen.onFeed ?? isPublic;
  const styleLabel = STYLE_PROFILES[gen.request.stylePreset]?.label ?? gen.request.stylePreset;
  const variantCount = gen.images.length;
  const ratio = gen.request.aspectRatio?.replace(':', ' / ');

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onOpen}
        className="block w-full rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
        aria-label={`Open gilding, ${styleLabel}, ${variantCount} ${
          variantCount === 1 ? 'variant' : 'variants'
        }, ${isPublic ? 'on the feed' : 'private'}`}
      >
        <div
          className={cn(
            'relative overflow-hidden rounded-lg bg-surface-sunken',
            'shadow-[0_14px_38px_-18px_rgba(0,0,0,0.7)] transition-[transform,box-shadow] duration-300 ease-out',
            'group-hover:-translate-y-1.5 group-hover:shadow-[0_30px_64px_-22px_rgba(0,0,0,0.86)]',
          )}
          style={ratio ? { aspectRatio: ratio } : undefined}
        >
          {firstImage ? (
            <WallImage
              src={firstImage}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.045]"
            />
          ) : (
            <div className="flex aspect-square w-full items-center justify-center text-ink-subtle">
              <span className="font-display text-caption italic">&middot;</span>
            </div>
          )}
          {/* gilt frame — a fine gold hairline that warms on hover */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-lg shadow-[inset_0_0_0_1px_rgba(233,200,115,0.26)] transition-shadow duration-300 group-hover:shadow-[inset_0_0_0_1.5px_rgba(233,200,115,0.62),inset_0_1px_0_rgba(255,247,220,0.35)]"
          />
          {/* a whisper of depth at the foot on hover */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/35 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          />
          {onFeed && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-0 top-0 h-0 w-0"
              style={{ borderTop: '13px solid rgb(var(--color-accent))', borderLeft: '13px solid transparent' }}
            />
          )}
        </div>
      </button>

      <CardMenu
        genId={gen.id}
        isPublic={isPublic}
        mutating={mutating}
        onPublish={onPublish}
        onUnpublish={onUnpublish}
        onDelete={onDelete}
      />
    </div>
  );
}

/** Spread items left-to-right across N columns (newest fills the top row). */
function buildColumns(items: GenWithCounts[], cols: number): GenWithCounts[][] {
  const out: GenWithCounts[][] = Array.from({ length: cols }, () => []);
  items.forEach((g, i) => {
    const col = out[i % cols];
    if (col) col.push(g);
  });
  return out;
}

// ----------------------------------------------------------------------------
// WallImage — a gallery tile bitmap that loads SEAMLESSLY.
//
// The wall serves full-resolution images, and a dense wall puts dozens on screen
// at once. Loaded naively that's 50 full-res bitmaps all fighting for bandwidth,
// so none finish and every tile sits blank for ages. Instead:
//   • IntersectionObserver — a tile only starts loading as it nears the viewport,
//     so even a 500-piece gallery never pulls everything at once.
//   • a small global concurrency gate — at most MAX_TILE_LOADS images download at
//     a time, so the FIRST screen fills in seconds and the rest stream in behind.
//   • a shimmering skeleton until the bitmap decodes, then a soft develop-in, so
//     a tile is never an empty void.
// ----------------------------------------------------------------------------

const MAX_TILE_LOADS = 6;
let activeTileLoads = 0;
const tileLoadQueue: Array<() => void> = [];

function acquireTileSlot(): Promise<void> {
  if (activeTileLoads < MAX_TILE_LOADS) {
    activeTileLoads += 1;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => tileLoadQueue.push(resolve));
}
function releaseTileSlot(): void {
  const next = tileLoadQueue.shift();
  if (next) next(); // hand the slot straight to the next waiter (count unchanged)
  else activeTileLoads = Math.max(0, activeTileLoads - 1);
}

function WallImage({ src, className }: { src: string; className: string }) {
  const hostRef = useRef<HTMLSpanElement>(null);
  const [show, setShow] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const acquired = useRef(false);
  const released = useRef(false);

  const release = useCallback(() => {
    if (acquired.current && !released.current) {
      released.current = true;
      releaseTileSlot();
    }
  }, []);

  useEffect(() => {
    const el = hostRef.current;
    let cancelled = false;
    let io: IntersectionObserver | null = null;

    const begin = (): void => {
      void acquireTileSlot().then(() => {
        acquired.current = true;
        if (cancelled) {
          release();
          return;
        }
        setShow(true);
      });
    };

    if (!el || typeof IntersectionObserver === 'undefined') {
      begin();
    } else {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io?.disconnect();
            io = null;
            begin();
          }
        },
        // Start the fetch a screen early so tiles are decoded by the time they're
        // scrolled into view.
        { rootMargin: '700px 0px' },
      );
      io.observe(el);
    }
    return () => {
      cancelled = true;
      io?.disconnect();
      release(); // free the slot if we unmount mid-load
    };
  }, [release]);

  const done = useCallback(() => {
    setLoaded(true);
    release(); // hand the slot to the next tile the instant this one decodes
  }, [release]);

  return (
    <>
      <span
        ref={hostRef}
        aria-hidden="true"
        className={cn(className, 'tile-skeleton transition-opacity duration-500', loaded && 'opacity-0')}
      />
      {show && (
        <img
          src={src}
          alt=""
          decoding="async"
          onLoad={done}
          onError={done}
          className={cn(className, 'img-develop', loaded && 'is-loaded')}
        />
      )}
    </>
  );
}

// ----------------------------------------------------------------------------
// CardMenu — ellipsis with publish/unpublish + a guarded delete
// ----------------------------------------------------------------------------

function CardMenu({
  genId,
  isPublic,
  mutating,
  onPublish,
  onUnpublish,
  onDelete,
  onOpenChange,
}: {
  genId: string;
  isPublic: boolean;
  mutating: boolean;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  // Delete is a two-step action: the menu swaps to a confirm panel so a stray
  // tap can never erase a piece.
  const [confirming, setConfirming] = useState(false);
  // Which way the dropdown unfurls so it never runs off-screen.
  const [openRight, setOpenRight] = useState(true);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const setOpenState = (next: boolean): void => {
    if (next) {
      const r = buttonRef.current?.getBoundingClientRect();
      // Tile on the left half → unfurl rightward; on the right half → leftward.
      if (r) setOpenRight(r.left < window.innerWidth / 2);
    }
    setOpen(next);
    onOpenChange?.(next);
  };

  const close = (): void => {
    setOpenState(false);
    setConfirming(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const stop = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Card actions"
        onClick={(e) => {
          stop(e);
          setOpenState(!open);
          setConfirming(false);
        }}
        className={cn(
          // Quiet glass disc. The static grid keeps it reliably clickable, so it
          // appears on card hover (desktop) and sits at a faint rest opacity on
          // touch — always tappable, never in the way.
          'absolute right-2 top-2 z-10 inline-flex h-7 w-7 items-center justify-center',
          'rounded-full bg-ink/50 text-white/90 backdrop-blur-[3px] transition-[opacity,background-color] duration-200',
          'hover:bg-ink/70 hover:text-white',
          open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 max-md:opacity-60',
        )}
      >
        <MoreIcon />
      </button>

      {open && (
        <div
          role="menu"
          onClick={stop}
          className={cn(
            'sheet-enter absolute top-9 z-20 w-56 max-w-[calc(100vw-1rem)] overflow-hidden',
            openRight ? 'left-1.5' : 'right-1.5',
            'rounded-md border border-hairline-strong bg-surface-elevated p-1.5',
            'shadow-lift',
          )}
        >
          {confirming ? (
            <div className="flex flex-col gap-2 p-2">
              <p className="font-display text-micro font-medium text-ink">
                Delete this gilding?
              </p>
              <p className="font-display italic text-caption text-ink-subtle">
                This permanently removes it from your gallery{isPublic ? ' and the feed' : ''}. It
                can’t be undone.
              </p>
              <div className="mt-1 flex items-center gap-2">
                <button
                  type="button"
                  disabled={mutating}
                  onClick={(e) => {
                    stop(e);
                    onDelete();
                    close();
                  }}
                  className={cn(
                    'flex-1 rounded-xs px-3 py-2 text-center font-body text-micro font-medium',
                    'bg-danger/12 text-danger transition-colors duration-150',
                    'hover:bg-danger/20 disabled:opacity-50',
                  )}
                >
                  {mutating ? 'Deleting…' : 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    stop(e);
                    setConfirming(false);
                  }}
                  className="flex-1 rounded-xs px-3 py-2 text-center font-body text-micro text-ink-soft transition-colors duration-150 hover:bg-surface-sunken"
                >
                  Keep
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Share is only offered once a piece is on the feed — a private
                  piece has no public page to hand out. */}
              {isPublic && (
                <>
                  <ShareButton id={genId} variant="menu" />
                  <div className="my-1 h-px bg-hairline/70" aria-hidden="true" />
                </>
              )}
              <MenuItem
                icon={isPublic ? <LockIcon /> : <EyeIcon className="text-accent-deep" />}
                label={
                  mutating
                    ? isPublic
                      ? 'Updating…'
                      : 'Publishing…'
                    : isPublic
                      ? 'Take off the feed'
                      : 'Publish to the feed'
                }
                disabled={mutating}
                onClick={() => {
                  if (isPublic) onUnpublish();
                  else onPublish();
                  close();
                }}
              />
              <div className="my-1 h-px bg-hairline/70" aria-hidden="true" />
              <MenuItem
                icon={<TrashIcon />}
                label="Delete from gallery"
                danger
                onClick={() => setConfirming(true)}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-xs px-3 py-2.5 text-left',
        'font-body text-micro transition-colors duration-150 disabled:opacity-50',
        danger ? 'text-danger hover:bg-danger/8' : 'text-ink hover:bg-surface-sunken',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 6h12" />
      <path d="M8 6V4.5A1 1 0 0 1 9 3.5h2a1 1 0 0 1 1 1V6" />
      <path d="M5.5 6l.7 9a1.5 1.5 0 0 0 1.5 1.4h4.6a1.5 1.5 0 0 0 1.5-1.4l.7-9" />
      <path d="M8.5 9.5v4M11.5 9.5v4" />
    </svg>
  );
}

// ----------------------------------------------------------------------------
// Utilities
// ----------------------------------------------------------------------------

